import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import SalonFlag from 'components/core/SalonFlag.component';
import WhenSalonFlag from 'components/core/WhenSalonFlag.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import FLAG from 'config/flag.singleton';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import linkTo from 'utils/links/linkTo.util';

import CN from './HumanResourcesMenu.module.scss';

const MenuItem = ({ icon, children, disabled, ...rest }) => {
  const menuItemDisabledClass = disabled ? CN.menuItemDisabled : CN.menuItem;
  return (
    <NavLink {...rest} className={menuItemDisabledClass} activeClassName={CN.selected}>
      <div className={CN.hoverContainer}>
        <div className={CN.menuText}>
          <div className={cn(CN[`${icon}Icon`])} />
          <div>{children}</div>
        </div>
      </div>
    </NavLink>
  );
};

const HumanResourcesMenu = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const isFromMobile = useIsFromMobileApp();

  const linkHr = linkTo({ pattern: LINK.hrSplash, params: { salonId }, fault: '#' });
  const linkEditKpi = linkTo({ pattern: LINK.hrEditKpi, params: { salonId }, fault: '#' });
  const linkTimeOff = linkTo({ pattern: LINK.hrTimeoff, params: { salonId }, fault: '#' });
  const linkTargets = linkTo({ pattern: LINK.hrTargets, params: { salonId }, fault: '#' });
  const linkCommissions = linkTo({ pattern: LINK.hrCommissions, params: { salonId }, fault: '#' });

  const renderedTooltipPlan = (
    <div>
      {S.star}
      {S.space}
      {t('humanResources.headerMenu.tooltipPlanText')}
    </div>
  );

  const renderedTooltipComingSoon = (
    <div>
      {S.partyingFace}
      {S.space}
      {t('humanResources.headerMenu.tooltipComingSoonText')}
    </div>
  );

  return (
    <div
      data-bem="HumanResourcesMenu"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="HumanResourcesMenu__sub-menu" className={CN.container}>
        {!isFromMobile && (
          <MenuItem icon="hr" to={linkHr}>
            {t('humanResources.headerMenu.hrOverview')}
          </MenuItem>
        )}
        <Permitted value={PERM.canEditKpisPersonal} tag="HumanResourcesMenu__kpi--personal">
          <MenuItem icon="kpi" to={linkEditKpi}>
            {t('humanResources.headerMenu.customiseKpis')}
          </MenuItem>
        </Permitted>
        <Permitted value={PERM.canEditKpisAllStaff} tag="HumanResourcesMenu__kpi--all">
          <MenuItem icon="kpi" to={linkEditKpi}>
            {t('humanResources.headerMenu.customiseKpis')}
          </MenuItem>
        </Permitted>
        {FLAG.tabTargets && (
          <Tooltip variant="comingSoon" position="bottom" content={renderedTooltipComingSoon}>
            <MenuItem icon="targets" to={linkTargets} disabled={true}>
              {t('humanResources.headerMenu.staffTargets')}
            </MenuItem>
          </Tooltip>
        )}

        <WhenSalonFlag disabled name={SF.timeOffCats}>
          <Tooltip variant="primaryBottom" content={renderedTooltipPlan}>
            <MenuItem icon="time" to={linkTimeOff} disabled={true}>
              {t('humanResources.headerMenu.timeOffAllowances')}
            </MenuItem>
          </Tooltip>
        </WhenSalonFlag>

        <SalonFlag name={SF.timeOffCats}>
          <SalonFlag.IfEnabled>
            <MenuItem icon="time" to={linkTimeOff}>
              {t('humanResources.headerMenu.timeOffAllowances')}
            </MenuItem>
          </SalonFlag.IfEnabled>
        </SalonFlag>
        <Permitted not value={PERM.canEditStaffCommissions} tag="HumanResourcesMenu__can-not-edit-commissions">
          <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
            <MenuItem icon="money" to={linkCommissions} disabled={true}>
              {t('humanResources.headerMenu.splitTakings')}
            </MenuItem>
          </Tooltip>
        </Permitted>
        <Permitted value={PERM.canEditStaffCommissions} tag="HumanResourcesMenu__can-edit-commissions">
          <MenuItem icon="money" to={linkCommissions}>
            {t('humanResources.headerMenu.splitTakings')}
          </MenuItem>
        </Permitted>
      </div>
    </div>
  );
};
export default HumanResourcesMenu;
